import { journalTemplatesData } from './journalTemplates.data';

// Type definitions matching the JSON structure
export interface JournalTemplateSource {
  url: string;
  title: string;
  lastChecked: string;
}

export interface JournalTemplateSizePolicy {
  mode: 'columns' | 'range' | 'presets';
  widths_mm?: number[];
  width_mm_range?: [number, number];
  maxHeight_mm?: number | null;
  presets_cm?: {
    small?: [number, number];
    medium?: [number, number];
    large?: [number, number];
  };
}

export interface JournalTemplateDpiPolicy {
  lineArt: number;
  halftone: number;
  combination: number | null;
  final: number | null;
  raster_min: number;
  raster_recommended_lineart: number;
}

export interface JournalTemplateTypography {
  fontFamily: string[];
  fontSizePt_min: number;
  fontSizePt_max: number;
  fontSizePt_range: [number, number];
  panelLabelPt: number;
  lineWeightPt_min: number;
  lineWeightPt_max: number;
  lineWeightPt_range: [number, number];
}

export interface JournalTemplateColorPolicy {
  mode: 'RGB' | 'CMYK' | 'CMYK_or_RGB' | 'RGB_or_CMYK' | 'RGB_or_Grayscale';
  notes: string;
}

export interface JournalTemplateFileFormats {
  preferred: string[];
  alsoAccepted: string[];
  vectorPreferred: boolean;
}

export interface JournalTemplate {
  id: string;
  label: string;
  publisher: string;
  sizePolicy: JournalTemplateSizePolicy;
  dpiPolicy: JournalTemplateDpiPolicy;
  typography: JournalTemplateTypography;
  colorPolicy: JournalTemplateColorPolicy;
  fileFormats: JournalTemplateFileFormats;
  sources: JournalTemplateSource[];
  notes: string;
}

// Export the type for JournalTemplateId
export type JournalTemplateId =
  | 'nature'
  | 'science'
  | 'cell'
  | 'pnas'
  | 'acs_generic'
  | 'plos_generic'
  | 'elsevier_generic'
  | 'wiley_generic'
  | 'science_advances';

// Get the templates array from the data
const templates = journalTemplatesData.journalTemplates as unknown as JournalTemplate[];

// Create a backward-compatible interface that includes both old and new properties
interface BackwardCompatibleTemplate extends JournalTemplate {
  // Backward compatibility: map 'label' to 'name' for existing code
  name: string;
  // Optional fields for backward compatibility
  baseStyle?: string | null;
  specialRules?: string[];
}

// Transform templates to include backward-compatible properties
const journalTemplatePresets: BackwardCompatibleTemplate[] = templates.map((template) => ({
  ...template,
  name: template.label, // Map label to name for backward compatibility
  baseStyle: null, // Not in new structure, but some code may check for it
  specialRules: [], // Not in new structure, but some code may check for it
}));

export { journalTemplatePresets };

// Export helper functions to access templates
export function getJournalTemplate(id: JournalTemplateId): JournalTemplate | undefined {
  return templates.find((t) => t.id === id);
}

export function getAllJournalTemplates(): JournalTemplate[] {
  return templates;
}
